import { FC, useCallback } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useLiveQuery } from "dexie-react-hooks";
import {
  SettingsIntegrationPageWrapper,
  SettingsIntegrationHeader,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";
import {
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSButton,
  MDSButtonSlotName,
  MDSIcon,
  MDSIconKind,
  MDSIconColor,
  MDSTextColor,
  MDSListBox,
} from "@/mds";
import { useAppStore } from "@/store";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import {
  formatDateString,
  generateRelativePrepositionAdverbialDateString,
  generateShortDateString,
} from "@/domains/date/date";
import { DataExportObservable } from "@/store/data-exports/DataExportObservable";
import { DateTime } from "luxon";
import { api } from "@/modules/api";
import { fileModule } from "@/modules/file";

const handleDownloadExport = async (dataExport: DataExportObservable) => {
  const response = await api.get("/v2/data-exports/{data_export_id}/download-url", {
    params: { path: { data_export_id: dataExport.id } },
  });

  if (response.data?.url) {
    fileModule.downloadFileFromUrlDirectly({
      fileName: `mem-export-${generateShortDateString(DateTime.fromJSDate(dataExport.startedAt))}.zip`,
      downloadUrl: response.data.url,
      inTab: false,
    });
  }
};

function buildExportListItems(exports: DataExportObservable[]) {
  return exports.map(dataExport => {
    const exportDate = DateTime.fromJSDate(dataExport.startedAt);

    if (dataExport.isRunning) {
      return {
        id: dataExport.id,
        children: (
          <StyledExportItemContent>
            <MDSIcon kind={MDSIconKind.Spinner} />
            <MDSText variant={MDSTextVariant.TextS}>
              Export started {generateRelativePrepositionAdverbialDateString(exportDate)}
            </MDSText>
          </StyledExportItemContent>
        ),
      };
    }

    return {
      id: dataExport.id,
      children: (
        <StyledExportItemContent>
          <MDSIcon
            kind={dataExport.didFail ? MDSIconKind.Danger : MDSIconKind.CheckCircleSolid}
            color={dataExport.didFail ? MDSIconColor.Danger : MDSIconColor.Positive}
          />
          <MDSText variant={MDSTextVariant.TextS}>
            {dataExport.didFail
              ? "Export failed"
              : `Export on ${formatDateString(exportDate, "LLLL d, yyyy")}`}
          </MDSText>
        </StyledExportItemContent>
      ),
      rightAddon: (
        <MDSButton
          onClick={() => void handleDownloadExport(dataExport)}
          isDisabled={dataExport.didFail}
        >
          Download
        </MDSButton>
      ),
    };
  });
}

export const SettingsExportsModalPage: FC = observer(() => {
  const { store } = useAppStore();
  const exportIsRunning = store.dataExports.isExportInProgress;
  const exportsAreReady = store.dataExports.isReady;

  const noteCount = useLiveQuery(
    () => store.notes.localTable.where("is_available").equals(1).count(),
    [],
    0
  );

  const collectionCount = useLiveQuery(
    () => store.collections.localTable.where("is_available").equals(1).count(),
    [],
    0
  );

  const handleExportClick = useCallback(async () => {
    trackEvent(TrackedEvent.ExportCreate);
    store.dataExports.createExport();
  }, [store.dataExports]);

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader breadcrumb="Account" title="Exports" />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          <StyledSectionLabel variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
            YOUR DATA
          </StyledSectionLabel>
          <StyledDataCounts>
            <StyledDataColumn>
              <MDSText variant={MDSTextVariant.HeaderXl}>{noteCount}</MDSText>
              <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                Notes
              </MDSText>
            </StyledDataColumn>
            <StyledDataColumn>
              <MDSText variant={MDSTextVariant.HeaderXl}>{collectionCount}</MDSText>
              <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                Collections
              </MDSText>
            </StyledDataColumn>
          </StyledDataCounts>
        </SettingsIntegrationSection>

        <SettingsIntegrationSection>
          <StyledSectionLabel variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
            EXPORT YOUR DATA
          </StyledSectionLabel>
          <StyledExportDescription>
            <MDSText variant={MDSTextVariant.ParagraphS}>
              Export your notes and collections as Markdown files. How it works:
            </MDSText>
            <StyledList>
              <li>Your individual notes will each become a separate Markdown file</li>
              <li>
                Collections will become Markdown files that include all notes from that collection
              </li>
              <li>
                Any images or attachments in your notes will be saved in an &quot;attachments&quot;
                folder
              </li>
              <li>Everything will be packaged in a zip file ready for download</li>
            </StyledList>
          </StyledExportDescription>
          <StyledExportButton
            onClick={handleExportClick}
            isDisabled={!exportsAreReady || exportIsRunning}
          >
            <MDSIcon kind={MDSIconKind.ArrowDownToLine} slot={MDSButtonSlotName.LeftIcon} />
            Export all notes and collections
          </StyledExportButton>
        </SettingsIntegrationSection>

        <SettingsIntegrationSection>
          <StyledSectionLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            style={{ marginBottom: 20 }}
          >
            YOUR EXPORTS
          </StyledSectionLabel>
          {!exportsAreReady ? (
            <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Tertiary}>
              Loading exports…
            </MDSText>
          ) : store.dataExports.length === 0 ? (
            <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Tertiary}>
              Your export history will appear here
            </MDSText>
          ) : (
            <MDSListBox items={buildExportListItems(store.dataExports.getLatestExports(3))} />
          )}
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
});

const StyledSectionLabel = styled(MDSText)`
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const StyledDataCounts = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const StyledDataColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledExportDescription = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-s"]};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const StyledList = styled.ul`
  margin: 0;
  margin-top: ${({ theme }) => theme.spacing[2]};
  padding-left: ${({ theme }) => theme.spacing[5]};
`;

const StyledExportButton = styled(MDSButton)`
  align-self: flex-start;
  margin-top: ${({ theme }) => theme.spacing[3]};
`;

const StyledExportItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;
